import { useState } from "react";
import { Send, CheckCircle, AlertCircle, Mail, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { FadeIn, TextReveal } from "../components/Animations";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const whoWeWantToHearFrom = [
  "Governments ready to adopt innovations at scale",
  "Innovators with solutions that need a pathway to millions",
  "Investors who want impact, not just reports",
  "Researchers who want their work to reach clinics",
  "Professional associations shaping clinical practice",
  "Implementers with hard-won frontline wisdom",
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", organization: "", subject: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { 
      setStatus("error"); 
      setErrorMsg("Please fill in all required fields."); 
      return; 
    }
    setStatus("loading");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      setForm({ name: "", email: "", organization: "", subject: "", message: "" });
    } catch { 
      setStatus("error"); 
      setErrorMsg("Something went wrong. Please try again."); 
    }
  };

  return (
    <div data-testid="contact-page" className="pt-16">
      {/* Hero Section - Compact with key message upfront */}
      <section className="bg-navy py-20 md:py-28 text-center">
        <div className="max-w-4xl mx-auto px-6">
          <FadeIn>
            <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-8">
              <Mail className="text-ochre-light" size={28} />
            </div>
          </FadeIn>
          <FadeIn delay={0.2}>
            <p className="text-white/40 font-body text-xs tracking-[0.3em] uppercase mb-6">Get In Touch</p>
          </FadeIn>
          <TextReveal delay={0.3}>
            <h1 data-testid="contact-title" className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold text-white leading-[1] mb-6 tracking-tight">
              We Want to Hear From You
            </h1>
          </TextReveal>
          <FadeIn delay={0.5}>
            <p className="text-white/50 font-body text-lg md:text-xl leading-relaxed max-w-2xl mx-auto">
              Whether you are a government health leader, an innovator with a solution, or a funder looking for impact—we built this for you.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Who We Want to Hear From - Moved up before the form */}
      <section className="bg-[#fafafa] py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {whoWeWantToHearFrom.map((item, i) => (
              <FadeIn key={item} delay={i * 0.05}>
                <motion.div 
                  className="bg-white p-6 rounded-xl text-center h-full flex items-center justify-center"
                  whileHover={{ y: -3, scale: 1.02 }}
                >
                  <p className="text-navy font-body text-sm leading-relaxed">{item}</p>
                </motion.div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="bg-white py-24">
        <div className="max-w-4xl mx-auto px-6">
          <FadeIn>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-navy mb-4 tracking-tight text-center">Drop Us a Line</h2>
            <p className="text-slate-500 font-body text-base mb-12 text-center max-w-xl mx-auto">
              Tell us where you are, and we will show you what is possible.
            </p>
          </FadeIn>

          {status === "success" ? (
            <FadeIn>
              <motion.div 
                className="bg-emerald-50 p-12 text-center rounded-2xl" 
                data-testid="contact-success"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
              >
                <CheckCircle className="text-emerald-500 mx-auto mb-6" size={48} />
                <h3 className="font-heading text-xl font-bold text-emerald-800 mb-2">Message Sent</h3>
                <p className="text-emerald-600 font-body text-sm mb-6">Thank you for reaching out. We will get back to you soon.</p>
                <button 
                  onClick={() => setStatus("idle")} 
                  className="text-emerald-600 font-body text-sm font-medium underline" 
                  data-testid="send-another-btn"
                >
                  Send another message
                </button>
              </motion.div>
            </FadeIn>
          ) : (
            <FadeIn delay={0.1}>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="text-navy/60 font-body text-xs tracking-wide uppercase mb-3 block">
                      Full Name <span className="text-red-400">*</span>
                    </Label>
                    <Input 
                      id="name" 
                      name="name" 
                      value={form.name} 
                      onChange={handleChange} 
                      placeholder="Your full name" 
                      data-testid="contact-name-input" 
                      className="h-12 bg-[#fafafa] border-0 focus:ring-2 focus:ring-navy/20 rounded-xl px-4 font-body" 
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-navy/60 font-body text-xs tracking-wide uppercase mb-3 block">
                      Email <span className="text-red-400">*</span>
                    </Label>
                    <Input 
                      id="email" 
                      name="email" 
                      type="email" 
                      value={form.email} 
                      onChange={handleChange} 
                      placeholder="your@email.com" 
                      data-testid="contact-email-input" 
                      className="h-12 bg-[#fafafa] border-0 focus:ring-2 focus:ring-navy/20 rounded-xl px-4 font-body" 
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="organization" className="text-navy/60 font-body text-xs tracking-wide uppercase mb-3 block">
                      Organization
                    </Label>
                    <Input 
                      id="organization" 
                      name="organization" 
                      value={form.organization} 
                      onChange={handleChange} 
                      placeholder="Your organization" 
                      data-testid="contact-org-input" 
                      className="h-12 bg-[#fafafa] border-0 focus:ring-2 focus:ring-navy/20 rounded-xl px-4 font-body" 
                    />
                  </div>
                  <div>
                    <Label htmlFor="subject" className="text-navy/60 font-body text-xs tracking-wide uppercase mb-3 block">
                      Subject
                    </Label>
                    <Input 
                      id="subject" 
                      name="subject" 
                      value={form.subject} 
                      onChange={handleChange} 
                      placeholder="Subject" 
                      data-testid="contact-subject-input" 
                      className="h-12 bg-[#fafafa] border-0 focus:ring-2 focus:ring-navy/20 rounded-xl px-4 font-body" 
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="message" className="text-navy/60 font-body text-xs tracking-wide uppercase mb-3 block">
                    Message <span className="text-red-400">*</span>
                  </Label>
                  <Textarea 
                    id="message" 
                    name="message" 
                    value={form.message} 
                    onChange={handleChange} 
                    placeholder="Tell us how you would like to get involved..." 
                    rows={6} 
                    data-testid="contact-message-input" 
                    className="bg-[#fafafa] border-0 focus:ring-2 focus:ring-navy/20 rounded-xl px-4 py-3 font-body resize-none" 
                  />
                </div>
                
                {status === "error" && (
                  <div className="flex items-center gap-2 text-red-500 text-sm font-body" data-testid="contact-error">
                    <AlertCircle size={16} />{errorMsg}
                  </div>
                )}
                
                <div className="flex justify-center">
                  <Button 
                    type="submit" 
                    disabled={status === "loading"} 
                    data-testid="contact-submit-btn" 
                    className="bg-navy hover:bg-navy-800 text-white px-10 py-6 rounded-full text-base font-body font-medium transition-all duration-300 hover:shadow-xl hover:scale-105"
                  >
                    {status === "loading" ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Sending...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Send size={18} />Send Message
                      </span>
                    )}
                  </Button>
                </div>
              </form>
            </FadeIn>
          )}
        </div>
      </section>

      {/* Why This Matters - Bottom section */}
      <section className="bg-[#fafafa] py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <FadeIn>
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-navy mb-6 tracking-tight">Why This Matters</h3>
            <p className="text-slate-500 font-body text-base leading-relaxed max-w-2xl mx-auto">
              Twenty million babies are born too small every year. The solutions exist. The evidence is there. 
              What is missing is the machinery to get proven innovations into the health systems where they are needed most. 
              That is what we build.
            </p>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
